import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../utils/api';

const POLL_INTERVAL = 600_000;

const TYPE_CONFIG = {
  order_submitted:           { icon: 'bi-bag-plus-fill',         bg: '#cff4fc', color: '#055160' },
  order_updated:             { icon: 'bi-pencil-square',          bg: '#fff3cd', color: '#664d03' },
  order_rejected:            { icon: 'bi-x-circle-fill',          bg: '#f8d7da', color: '#842029' },
  qsc_report_submitted:      { icon: 'bi-clipboard2-check-fill',  bg: '#d1e7dd', color: '#0a3622' },
  qsc_improvement_submitted: { icon: 'bi-arrow-up-circle-fill',   bg: '#e2d9f3', color: '#432874' },
  qsc_improvement_approved:  { icon: 'bi-patch-check-fill',       bg: '#d1e7dd', color: '#0a3622' },
  qsc_improvement_rejected:  { icon: 'bi-patch-exclamation-fill', bg: '#fde8d8', color: '#7a3c10' }
};

function relativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'เมื่อกี้';
  if (mins < 60) return `เมื่อ ${mins} นาทีที่แล้ว`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `เมื่อ ${hrs} ชั่วโมงที่แล้ว`;
  const days = Math.floor(hrs / 24);
  return `${days} วันที่แล้ว`;
}

const NotificationPanel = () => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnreadCount(res.data.count);
    } catch {
      // ignore network errors silently
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll({ limit: 20 });
      setNotifications(res.data.data);
      setLoaded(true);
    } catch {
      // ignore
    }
  }, []);

  // Poll for unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  // Load notifications when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItemClick = async (notif) => {
    if (!notif.isRead) {
      try {
        await notificationsAPI.markRead(notif.id);
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {
        // ignore
      }
    }
    setOpen(false);
    if (notif.link && notif.link.startsWith('/')) navigate(notif.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const cfg = (type) => TYPE_CONFIG[type] || { icon: 'bi-bell-fill', bg: '#e9ecef', color: '#495057' };

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        className="notif-bell-btn"
        onClick={() => setOpen(prev => !prev)}
        aria-label="การแจ้งเตือน"
      >
        <i className="bi bi-bell-fill"></i>
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span className="notif-dropdown-title">การแจ้งเตือน</span>
            {unreadCount > 0 && (
              <button className="notif-mark-all-btn" onClick={handleMarkAllRead}>
                อ่านทั้งหมด
              </button>
            )}
          </div>

          <div className="notif-list">
            {!loaded && (
              <div className="notif-empty">กำลังโหลด…</div>
            )}
            {loaded && notifications.length === 0 && (
              <div className="notif-empty">ไม่มีการแจ้งเตือน</div>
            )}
            {notifications.map(notif => {
              const { icon, bg, color } = cfg(notif.type);
              return (
                <button
                  key={notif.id}
                  className={`notif-item${notif.isRead ? '' : ' notif-item--unread'}`}
                  onClick={() => handleItemClick(notif)}
                >
                  <div className="notif-icon-wrap" style={{ background: bg, color }}>
                    <i className={`bi ${icon}`}></i>
                  </div>
                  <div className="notif-body">
                    <div className="notif-title">{notif.title}</div>
                    <div className="notif-msg">{notif.message}</div>
                    <div className="notif-time">{relativeTime(notif.createdAt)}</div>
                  </div>
                  {!notif.isRead && <span className="notif-dot"></span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
